import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Button, LoadingOrbit, Switch, Tab, TabList, Tabs } from '@chatsdet/ui';
import {
  ArrowClockwise,
  FileText,
  Globe,
  Lightning,
  PencilSimple,
  Trash,
  TreeStructure,
} from '@phosphor-icons/react';
import { SpecActions } from '../spec/SpecActions';
import type { FlowSpec } from '../spec/useFlowSpec';
import { VersionDropdown, type VersionSelection } from '../VersionDropdown';
import type { FlowView } from '../types';
import type { ApiSequenceActions } from '../api';
import type { FlowDesignerToolbarProps } from './FlowDesignerToolbar';

type VersionDropdownProps = React.ComponentProps<typeof VersionDropdown>;

export type FlowDesignerViewControlsProps = {
  workflowId: string | null;
  isPreviewingHistory: boolean;
  isAuthoring: boolean;
  flowView: FlowView;
  setFlowView: (view: FlowView) => void;
  hasApiSequences: boolean;
  workflowFlowType: FlowDesignerToolbarProps['workflowFlowType'];
  workflowApiOnly?: boolean;
  onApiOnlyToggle?: (apiOnly: boolean) => void;
  isBrowserSingleApiSequence: boolean;
  apiActions: ApiSequenceActions | null;
  flowSpec: FlowSpec | null;
  snapshots: VersionDropdownProps['snapshots'];
  revisions: VersionDropdownProps['revisions'];
  draftSnapshots: VersionDropdownProps['draftSnapshots'];
  previewingVersion: VersionSelection | null;
  handleVersionSelect: (selection: VersionSelection) => void;
  onSaveVersion?: () => void;
  isSaving: boolean;
};

export const FlowDesignerViewControls: React.FC<FlowDesignerViewControlsProps> = ({
  workflowId,
  isPreviewingHistory,
  isAuthoring,
  flowView,
  setFlowView,
  hasApiSequences,
  workflowFlowType,
  workflowApiOnly,
  onApiOnlyToggle,
  isBrowserSingleApiSequence,
  apiActions,
  flowSpec,
  snapshots,
  revisions,
  draftSnapshots,
  previewingVersion,
  handleVersionSelect,
  onSaveVersion,
  isSaving,
}) => (
  <>
    {workflowId && !isPreviewingHistory && !isAuthoring && (
      <div className="absolute top-[4.5rem] left-0 right-0 z-20 flex items-center justify-between px-4 py-1 pointer-events-none">
        <div className="flex items-center gap-3 pointer-events-auto">
          <Tabs
            selectedKey={flowView}
            onSelectionChange={(key) => setFlowView(key as FlowView)}
            className="w-auto"
          >
            <TabList>
              <Tab
                id="browser"
                isDisabled={workflowApiOnly === true}
                tooltip={workflowApiOnly ? 'This workflow is API-only' : undefined}
              >
                <Globe size={14} />
                Browser
              </Tab>
              {hasApiSequences && (
                <Tab id="api">
                  <Lightning size={14} weight="fill" />
                  API
                </Tab>
              )}
              {workflowFlowType === 'seed' && (
                <Tab id="lineage">
                  <TreeStructure size={14} />
                  Lineage
                </Tab>
              )}
              <Tab id="spec">
                <FileText size={14} />
                cofactor.md
              </Tab>
            </TabList>
          </Tabs>
          {onApiOnlyToggle &&
            (flowView === 'api' || (flowView === 'browser' && isBrowserSingleApiSequence)) && (
              <Switch
                isSelected={workflowApiOnly ?? false}
                onChange={onApiOnlyToggle}
                size="sm"
                label="API only"
              />
            )}
        </div>
        {flowView === 'api' && apiActions && (
          <div className="flex items-center gap-1 pointer-events-auto">
            <Button variant="icon" size="sm" iconOnly tooltip="Edit" onPress={apiActions.onEdit}>
              <PencilSimple size={16} />
            </Button>
            <Button
              variant="icon"
              size="sm"
              iconOnly
              tooltip="Regenerate"
              onPress={apiActions.onRegenerate}
            >
              <ArrowClockwise size={16} />
            </Button>
            <Button variant="icon" size="sm" iconOnly tooltip="Clear" onPress={apiActions.onClear}>
              <Trash size={16} />
            </Button>
          </div>
        )}
      </div>
    )}

    {flowView === 'spec' && flowSpec && (
      <div className="absolute bottom-4 right-4 z-20">
        <SpecActions fullSource={flowSpec.fullSource} fileName={flowSpec.fileName} />
      </div>
    )}

    <div className="absolute bottom-4 left-4 z-20 flex flex-col gap-2">
      <VersionDropdown
        snapshots={snapshots}
        revisions={revisions}
        draftSnapshots={draftSnapshots}
        previewingVersion={previewingVersion}
        onSelect={handleVersionSelect}
        onSaveVersion={onSaveVersion}
      />
      <AnimatePresence>
        {isSaving && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.15 }}
            className="flex items-center gap-2 text-sm text-muted-foreground"
          >
            <LoadingOrbit size="sm" />
            <span>Saving...</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  </>
);
